import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { notify, notifyError, AlertType } from 'modules/Common/ui/toast/api';
import {
  adminKeys,
  createOrganization,
  deleteOrganization,
  fetchOrganizations,
  updateOrganization,
  updateOrganizationPlan,
} from '../api/organizations';

/**
 * The organizations list query.
 *
 * The signature is `({ filters, pagination })` — the shape useCgTable hands its
 * `useData` hook — rather than a flat params object, so the page never assembles
 * the query key by hand and filters cannot drift out of it. `pagination` here is
 * the hook's INPUT memo, exactly `{ page, limit }`, not the richer bag it gives
 * CgTable, so spreading it into the key drags no function or server-derived total
 * along to churn the key.
 *
 * `placeholderData: keepPreviousData` does more than stop the table blanking: it
 * is the second, cheaper witness in useCgTable's loading rule (its D2). Drop it and
 * the table stays undimmed for the first render of every page change.
 */
export const useAdminOrganizations = ({ filters, pagination } = {}, options) => {
  const params = { ...filters, ...pagination };
  return useQuery({
    queryKey: adminKeys.list(params),
    queryFn: () => fetchOrganizations(params),
    placeholderData: keepPreviousData,
    ...options,
  });
};

/**
 * All four mutations invalidate the same root. A narrower key would be wrong here:
 * a create lands on page 1 under the current sort, a delete shifts every later
 * page, and a plan change alters a value the `planStatus` filter selects on — so
 * any of them can invalidate a page the user is not currently looking at.
 */
const useAdminMutation = ({ mutationFn, successTitle, successMessage }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
      if (successTitle) notify(successTitle, successMessage, AlertType.SUCCESS);
    },
    // Deliberately NO onError toast: the dialogs route field-level issues onto
    // their fields via useServerIssues and only toast what did not match. A toast
    // here would double every field error with a banner saying the same thing.
  });
};

/**
 * A 201 here means the Clerk organization, the Mongo row and the plan exist — and nothing
 * else. Creating an organization deliberately enrols nobody (D20), so it has no membership
 * outcome to report and the plain success toast is the whole truth.
 */
export const useCreateOrganization = () =>
  useAdminMutation({
    mutationFn: createOrganization,
    successTitle: 'Organization created',
  });

export const useUpdateOrganization = () =>
  useAdminMutation({
    mutationFn: ({ id, payload }) => updateOrganization(id, payload),
    successTitle: 'Organization updated',
  });

export const useDeleteOrganization = () =>
  useAdminMutation({
    mutationFn: (id) => deleteOrganization(id),
    successTitle: 'Organization deleted',
  });

export const useUpdateOrganizationPlan = () =>
  useAdminMutation({
    mutationFn: ({ id, payload }) => updateOrganizationPlan(id, payload),
    successTitle: 'Plan updated',
  });

export { notifyError };
